import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

export type CybersourceFormValues = {
  maintenanceType: string;
  justification: string;
  country: string;
  operatorId?: string;
  operatorName: string;
  staffId?: string;
  workingLocation: string;
  description: string;
  emailAddress: string;
  viewOnly?: boolean;
  reportView?: boolean;
  download?: boolean;
  others?: string;
};

type Props = {
  onCancel: () => void;
  onSubmit: (values: CybersourceFormValues) => void;
  open: boolean;
  values: Partial<CybersourceFormValues>;
  readonly?: boolean;
};

export const CybersourceForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<CybersourceFormValues>({
    maintenanceType: "",
    justification: "",
    country: "",
    operatorId: undefined,
    operatorName: "",
    staffId: undefined,
    workingLocation: "",
    description: "",
    emailAddress: "",
    viewOnly: undefined,
    reportView: undefined,
    download: undefined,
    others: undefined,
    ...values,
  });

  function onFormValueChange(name: any, value: any): void {
    if (name === "transactions") {
      form.set(Form.target.VALUES, {
        viewOnly: value === "viewOnly",
        reportView: value === "reportView",
        download: value === "download",
      });
      form.set(Form.target.ERRORS, {
        viewOnly: undefined,
        reportView: undefined,
        download: undefined,
      });
    } else {
      form.set(Form.target.VALUE, [name, value]);
      validate(name, value).then(([name, error]) => {
        form.set(Form.target.ERROR, [name, error]);
      });
    }
  }

  function validate(
    name: keyof CybersourceFormValues,
    value: CybersourceFormValues[typeof name]
  ): Promise<[keyof CybersourceFormValues, Error?]> {
    switch (name) {
      case "maintenanceType":
      case "justification":
      case "country":
      case "operatorName":
      case "workingLocation":
      case "description":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(value as string),
        ]);
      case "emailAddress":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEmail(value as string),
        ]);
      case "viewOnly":
      case "reportView":
      case "download":
        return Promise.resolve([
          name,
          Form.Validators.hasValue(
            form.values.viewOnly ||
              form.values.reportView ||
              form.values.download
          ),
        ]);
      default:
        return Promise.resolve([name]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as keyof CybersourceFormValues, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);

      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="CyberSource EBC Operation Privilege Form"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1. Information</Form.Title>
        <Form.Dropdown
          error={form.errors.maintenanceType}
          label="Maintenance Type"
          name="maintenanceType"
          onChange={onFormValueChange}
          options={["Add", "Change of Privilege"]}
          required
          size={6}
          value={form.values.maintenanceType}
        />
        <Form.LineBreak />
        <Form.Textarea
          error={form.errors.justification}
          label="Justification"
          maxLength={200}
          name="justification"
          onChange={onFormValueChange}
          placeholder="Please limit your answer to 200 characters only"
          required
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.justification}
        />
        <Form.Title>2. Operator Details</Form.Title>
        <Form.Textbox
          error={form.errors.country}
          label={{
            text: "CyberSource Country",
            tooltip: "e.g. gphkadmin_acct, gpsghadmin_acct",
          }}
          name="country"
          onChange={onFormValueChange}
          size={6}
          value={form.values.country}
        />
        <Form.Dropdown
          error={form.errors.workingLocation}
          label={{
            text: "Working Location",
            tooltip: "For GPAP staff only, e.g. Hong Kong, Philippines",
          }}
          name="workingLocation"
          onChange={onFormValueChange}
          options={["Hong Kong", "Philippines"]}
          size={6}
          value={form.values.workingLocation}
        />
        <Form.Textbox
          label={{
            text: "Operator ID",
            tooltip: "Login user name. For new operator, leave this blank.",
          }}
          name="operatorId"
          onChange={onFormValueChange}
          size={6}
          value={form.values.operatorId}
        />
        <Form.Textbox
          error={form.errors.description}
          label={{
            text: "Description",
            tooltip: "e.g. Research Analyst, Team Lead",
          }}
          name="description"
          onChange={onFormValueChange}
          size={6}
          value={form.values.description}
        />
        <Form.Textbox
          error={form.errors.operatorName}
          label={{
            text: "Operator Name",
            tooltip:
              "Staff Full Name (Last name in capital letter, then first name)",
          }}
          name="operatorName"
          onChange={onFormValueChange}
          size={6}
          value={form.values.operatorName}
        />
        <Form.Textbox
          error={form.errors.emailAddress}
          label="Email Address"
          name="emailAddress"
          onChange={onFormValueChange}
          size={6}
          value={form.values.emailAddress}
        />
        <Form.Textbox
          label={{
            text: "Staff ID",
            tooltip:
              "For GPAP staff, default to start with 'GP + Working Location'.",
          }}
          name="staffId"
          onChange={onFormValueChange}
          size={6}
          value={form.values.staffId}
        />
        <Form.Title>
          3. For Add / Change of Privilege only: Privilege (select only 1 out of
          the 3 options)
        </Form.Title>
        <Form.Radiogroup
          error={
            form.errors.viewOnly ||
            form.errors.reportView ||
            form.errors.download ||
            undefined
          }
          label="Transactions"
          name="transactions"
          onChange={onFormValueChange}
          options={[
            {
              value: "viewOnly",
              label: "Transaction View Only",
            },
            {
              value: "reportView",
              label: "Transaction and Report View",
            },
            {
              value: "download",
              label: "Transaction and Report View / Download",
            },
          ]}
          size={12}
          value={
            form.values.viewOnly
              ? "viewOnly"
              : form.values.reportView
              ? "reportView"
              : form.values.download
              ? "download"
              : ""
          }
        />
        <Form.LineBreak />
        <Form.Textarea
          error={form.errors.others}
          label="Others"
          maxLength={200}
          name="others"
          onChange={onFormValueChange}
          placeholder="Please limit your answer to 200 characters only"
          required
          rows={5}
          rowsMax={5}
          size={6}
          value={form.values.others}
        />
      </Form>
    </Wrapper>
  );
};
